import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
    LayoutDashboard, 
    Settings, 
    Users, 
    Mail, 
    LogOut, 
    Clock, 
    Search,
    Trash2,
    CheckCircle,
    XCircle,
    Filter,
    ArrowUpRight,
    MessageSquare,
    Eye,
    AlertCircle,
    ArrowRight,
    Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
    Dialog, 
    DialogContent, 
    DialogHeader, 
    DialogTitle, 
    DialogDescription,
    DialogFooter
} from "@/components/ui/dialog";
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export default function DashboardPage() {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const token = localStorage.getItem("admin_token");
                if (!token) {
                    navigate("/admin/login");
                    return;
                }

                const response = await axios.get(`${BACKEND_URL}/api/admin/submissions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSubmissions(response.data);
            } catch (error) {
                console.error("Failed to fetch submissions:", error);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    localStorage.removeItem("admin_token");
                    navigate("/admin/login");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSubmissions();
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        toast.success("Logged out successfully");
        navigate("/admin/login");
    };

    const handleDelete = async () => {
        if (!selectedSubmission) return;

        try {
            const token = localStorage.getItem("admin_token");
            await axios.delete(`${BACKEND_URL}/api/admin/submissions/${selectedSubmission.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubmissions(submissions.filter(s => s.id !== selectedSubmission.id));
            toast.success("Submission deleted successfully");
            setIsDeleteOpen(false);
        } catch (error) {
            console.error("Delete failed:", error);
            toast.error("Failed to delete submission");
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const token = localStorage.getItem("admin_token");
            await axios.put(`${BACKEND_URL}/api/admin/submissions/${id}`, 
                { status: status },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSubmissions(submissions.map(s => s.id === id ? { ...s, status: status } : s));
            toast.success(`Marked as ${status}`);
        } catch (error) {
            console.error("Update failed:", error);
            toast.error("Update failed");
        }
    };

    const filteredSubmissions = submissions.filter(s => 
        s.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.organization?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const stats = {
        total: submissions.length,
        new: submissions.filter(s => (s.status || 'new') === 'new').length,
        reviewed: submissions.filter(s => s.status === 'reviewed').length
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
    };

    return (
        <div className="flex min-h-screen bg-[#fcfdfe] font-['Manrope']">
            {/* Sidebar */}
            <aside className="w-[280px] bg-[#0f172a] text-white flex flex-col fixed h-full z-20 shadow-2xl">
                <div className="p-8 pb-10">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-[#243b7f] flex items-center justify-center font-bold text-lg border border-white/10 shadow-lg shadow-blue-900/40">V</div>
                        <h1 className="text-2xl font-bold tracking-tight text-white">VANCO AI</h1>
                    </div>
                </div>

                <nav className="flex-1 px-4 space-y-2">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 py-4">Menu</div>
                    
                    <Link to="/admin/dashboard" className="flex items-center gap-4 px-4 py-3.5 bg-gradient-to-r from-blue-600/20 to-transparent text-[#60a5fa] border-l-4 border-blue-500 rounded-lg transition-all">
                        <LayoutDashboard className="w-5 h-5" />
                        <span className="font-semibold">Dashboard</span>
                    </Link>
                    
                    <button className="w-full flex items-center gap-4 px-4 py-3.5 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all group">
                        <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        <span className="font-medium">Submissions</span>
                        <Badge className="ml-auto bg-blue-500/20 text-blue-400 hover:bg-blue-500/20 border-0">{stats.new}</Badge>
                    </button>


                </nav>

                <div className="p-4 border-t border-white/5">
                    <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-4 px-4 py-4 text-red-400 hover:text-white hover:bg-red-500/20 rounded-xl transition-all font-semibold"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Sign Out</span>
                        <ArrowRight className="w-4 h-4 ml-auto opacity-40" />
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 ml-[280px] p-10 bg-[#f8fbff]">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-12">
                    <div>
                        <h2 className="text-[32px] font-bold text-[#0f172a] -tracking-tight mb-2">Dashboard Overview</h2>
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>Last updated: {new Date().toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                    {[
                        { label: 'Total Requests', value: stats.total, color: 'blue', icon: Mail },
                        { label: 'Unread Inquiries', value: stats.new, color: 'amber', icon: MessageSquare },
                        { label: 'Processed', value: stats.reviewed, color: 'emerald', icon: CheckCircle },
                    ].map((item, idx) => (
                        <motion.div
                            key={item.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                        >
                            <Card className="border-0 shadow-lg shadow-blue-900/5 hover:translate-y-[-4px] transition-transform duration-300">
                                <CardContent className="p-8">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-14 h-14 rounded-2xl bg-${item.color}-500/10 flex items-center justify-center`}>
                                            <item.icon className={`w-7 h-7 text-${item.color}-600`} />
                                        </div>
                                        <div>
                                            <p className="text-slate-500 font-medium">{item.label}</p>
                                            <h3 className="text-3xl font-extrabold text-slate-900">{item.value}</h3>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Main Section */}
                <div className="bg-white rounded-[32px] border border-slate-100 shadow-2xl shadow-blue-900/5 p-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                            Recent Submissions
                            <Badge className="bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 border-0">{filteredSubmissions.length}</Badge>
                        </h3>

                        <div className="flex items-center gap-4">
                            <div className="relative w-80">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input 
                                    placeholder="Search by name, company..." 
                                    className="pl-12 h-14 border-slate-100 bg-slate-50/50 rounded-2xl focus:ring-blue-500/20 transition-all font-medium"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                        </div>
                    </div>

                    <div className="overflow-hidden rounded-2xl border border-slate-50">
                        <Table>
                            <TableHeader className="bg-slate-50/50">
                                <TableRow className="hover:bg-transparent border-0">
                                    <TableHead className="px-8 py-6 font-bold text-slate-900 uppercase tracking-wider text-[11px]">User</TableHead>
                                    <TableHead className="px-8 py-6 font-bold text-slate-900 uppercase tracking-wider text-[11px]">Contact Details</TableHead>
                                    <TableHead className="px-8 py-6 font-bold text-slate-900 uppercase tracking-wider text-[11px]">Company</TableHead>
                                    <TableHead className="px-8 py-6 font-bold text-slate-900 uppercase tracking-wider text-[11px] text-center">Inquiry Type</TableHead>
                                    <TableHead className="px-8 py-6 font-bold text-slate-900 uppercase tracking-wider text-[11px] text-center">Status</TableHead>
                                    <TableHead className="px-8 py-6 font-bold text-slate-900 uppercase tracking-wider text-[11px] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <AnimatePresence mode="popLayout">
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="py-24 text-center">
                                                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredSubmissions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="py-32 text-center text-slate-400 font-medium">
                                                No submissions found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredSubmissions.map((submission, index) => (
                                            <motion.tr
                                                key={submission.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="group border-b border-slate-50 last:border-0 hover:bg-blue-50/30 transition-colors"
                                            >
                                                <TableCell className="px-8 py-8">
                                                    <div className="font-bold text-slate-900 text-base">{submission.full_name}</div>
                                                    <div className="text-xs text-slate-500 font-medium tracking-wide mt-1">{submission.job_title || 'Lead'}</div>
                                                </TableCell>
                                                <TableCell className="px-8 py-8">
                                                    <div className="text-[13px] text-slate-600 font-semibold">{submission.email}</div>
                                                    <div className="text-xs text-slate-400 mt-1">{submission.phone || 'No phone'}</div>
                                                </TableCell>
                                                <TableCell className="px-8 py-8">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                                                            {(submission.organization || 'P').charAt(0)}
                                                        </div>
                                                        <span className="text-[13px] font-bold text-slate-700">{submission.organization || "Personal"}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="px-8 py-8 text-center">
                                                    <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 border-0 px-3 py-1.5 capitalize text-[11px] font-bold tracking-wider">
                                                        {submission.query_type}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="px-8 py-8 text-center">
                                                    <Badge className={`
                                                        ${submission.status === 'reviewed' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700 animate-pulse'} 
                                                        px-4 py-2 border-0 rounded-full text-[10px] font-black uppercase tracking-widest
                                                    `}>
                                                        {submission.status || 'new'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="px-8 py-8 text-right">
                                                    <div className="flex justify-end gap-3 translate-x-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                                                        <Button 
                                                            variant="ghost" 
                                                            className="w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                                                            onClick={() => {
                                                                setSelectedSubmission(submission);
                                                                setIsViewOpen(true);
                                                            }}
                                                        >
                                                            <Eye className="w-5 h-5" />
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            className="w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm text-slate-500 hover:text-red-500 hover:bg-red-50"
                                                            onClick={() => {
                                                                setSelectedSubmission(submission);
                                                                setIsDeleteOpen(true);
                                                            }}
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </motion.tr>
                                        ))
                                    )}
                                </AnimatePresence>
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </main>

            {/* View Details Dialog */}
            <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
                <DialogContent className="max-w-2xl bg-white p-0 rounded-[40px] overflow-hidden border-0 shadow-2xl">
                    <div className="bg-[#0f172a] p-10 text-white relative">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-[100px] pointer-events-none" />
                        <h2 className="text-3xl font-extrabold flex items-center gap-4 relative z-10">
                            <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center">
                                <Mail className="w-7 h-7" />
                            </div>
                            Inquiry Insights
                        </h2>
                    </div>

                    {selectedSubmission && (
                        <div className="p-10 space-y-10">
                            <div className="grid grid-cols-2 gap-10">
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">Prospect Name</label>
                                    <p className="text-xl font-bold text-slate-900">{selectedSubmission.full_name}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">Communication</label>
                                    <p className="text-xl font-bold text-blue-600">{selectedSubmission.email}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">Entity</label>
                                    <p className="text-lg font-bold text-slate-900">{selectedSubmission.organization || "Private Individual"}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">Time Arrived</label>
                                    <p className="text-lg font-bold text-slate-900">{formatDate(selectedSubmission.created_at)}</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400">Requested Information</label>
                                <div className="p-8 bg-slate-50/80 border border-slate-100 rounded-[24px] relative group overflow-hidden">
                                     <MessageSquare className="absolute -bottom-4 -right-4 w-24 h-24 text-blue-500/5 group-hover:scale-110 transition-transform duration-700" />
                                     <p className="text-slate-700 leading-relaxed font-medium text-lg italic">
                                        "{selectedSubmission.message}"
                                     </p>
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4">
                                <Button 
                                    className="flex-1 bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-600 hover:to-blue-500 text-white h-16 rounded-2xl shadow-xl shadow-blue-500/20 font-bold"
                                    onClick={() => {
                                        handleUpdateStatus(selectedSubmission.id, 'reviewed');
                                        setIsViewOpen(false);
                                    }}
                                >
                                    <CheckCircle className="w-5 h-5 mr-3" />
                                    Mark as Processed
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="flex-1 h-16 rounded-2xl border-slate-200 text-slate-600 font-bold hover:bg-slate-50"
                                    onClick={() => setIsViewOpen(false)}
                                >
                                    Close Portal
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <DialogContent className="max-w-md bg-white rounded-[32px] p-8">
                    <DialogHeader className="space-y-4">
                        <div className="w-20 h-20 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
                             <AlertCircle className="w-10 h-10 text-rose-500" />
                        </div>
                        <DialogTitle className="text-2xl font-black text-slate-900 text-center uppercase tracking-tight">
                             Safety check
                        </DialogTitle>
                        <DialogDescription className="text-center text-slate-500 font-medium">
                            Deleting this submission is permanent. Are you absolutely certain?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-3 mt-10">
                        <Button 
                            className="h-14 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold" 
                            onClick={handleDelete}
                        >
                            Confirm Destruction
                        </Button>
                        <Button 
                            variant="ghost" 
                            className="h-14 font-bold text-slate-500 hover:text-slate-900" 
                            onClick={() => setIsDeleteOpen(false)}
                        >
                            Nevermind
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
